import { supabase } from "@/shared/lib/supabase";
import type { ChatConversation } from "@/types/assistant.types";
import type { AssistantStreamCallbacks, AssistantStreamEvent } from "../types";
import {
  assistantFetchJson,
  getAssistantFunctionUrl,
  readErrorMessage,
} from "../lib/api";

type AssistantConversationResponse = {
  conversation: ChatConversation;
};

export type CreateConversationParams = {
  analysisId: string;
  accessToken: string;
  title?: string;
};

export type SendMessageParams = {
  conversationId: string;
  prompt: string;
  accessToken: string;
  callbacks: AssistantStreamCallbacks;
  abortController: AbortController;
};

export type UpdateConversationTitleParams = {
  conversationId: string;
  title: string;
};

export async function createConversation({
  analysisId,
  accessToken,
  title,
}: CreateConversationParams): Promise<ChatConversation> {
  const result = await assistantFetchJson<AssistantConversationResponse>(
    `/analysis/${encodeURIComponent(analysisId)}/conversations`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ title: title?.trim() || null }),
    },
  );

  return result.conversation;
}

function handleStreamEvent(
  event: AssistantStreamEvent,
  callbacks: AssistantStreamCallbacks,
): boolean {
  if (event.type === "chunk") {
    if (event.delta) {
      callbacks.onChunk(event.delta);
    }
    return false;
  }

  if (event.type === "error") {
    callbacks.onError(event.message);
    return false;
  }

  return event.type === "done";
}

export async function sendMessage({
  conversationId,
  prompt,
  accessToken,
  callbacks,
  abortController,
}: SendMessageParams): Promise<void> {
  const response = await fetch(
    getAssistantFunctionUrl(
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
    ),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ prompt }),
      signal: abortController.signal,
    },
  );

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  if (!response.body) {
    throw new Error("Assistant stream did not start correctly");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;

      const data = line.slice(6).trim();
      if (!data || data === "[DONE]") continue;

      try {
        const parsed = JSON.parse(data) as AssistantStreamEvent;
        sawDone = handleStreamEvent(parsed, callbacks) || sawDone;
      } catch (error) {
        console.warn("SSE parse error", error);
      }
    }
  }

  if (sawDone) {
    await callbacks.onDone();
  }
}

export async function updateConversationTitle({
  conversationId,
  title,
}: UpdateConversationTitleParams): Promise<void> {
  const { error } = await supabase
    .schema("assistant")
    .from("chat_conversation")
    .update({ title: title.trim() })
    .eq("conversation_id", conversationId)
    .is("record_deleted", null);

  if (error) {
    throw error;
  }
}

export const assistantMutations = {
  createConversation,
  sendMessage,
  updateConversationTitle,
};
